
  import { Component, Vue, Watch } from 'vue-property-decorator'
  import { mapGetters } from 'vuex'

@Component({
  computed: {
    ...mapGetters('user', ['disconnect']),
  },
})
  export default class DisconnectUser extends Vue {
  disconnect!: boolean

  // Controlar la visibilidad del dialog basado en el estado de desconexión
  get showDialog (): boolean {
    return this.disconnect
  }

  @Watch('disconnect', { immediate: true })
  onDisconnectChange (newVal: boolean) {
    // Recalcular posiciones cuando se muestra el dialog
    if (newVal) {
      this.resetPositions()
    }
  }

  // Resetear posiciones para recalcular en cada apertura
  resetPositions (): void {
    this.particlePositions = null
    this.sparkPositions = null
  }

  // Variables para cachear las posiciones calculadas
  private particlePositions: Array<{ left: number, top: number }> | null = null
  private sparkPositions: Array<{ left: string, top: string }> | null = null

  // Definir zonas de exclusión para elementos principales
  private getExclusionZones (): Array<{ left: number, top: number, width: number, height: number }> {
    return [
      // Zona del engranaje 1 (ampliar zona de protección)
      { left: 5, top: 0, width: 35, height: 35 },
      // Zona del engranaje 2 (ampliar zona de protección)
      { left: 55, top: 20, width: 35, height: 35 },
      // Zona del engranaje 3 (ampliar zona de protección)
      { left: 15, top: 55, width: 35, height: 35 },
      // Zona del camión (ajustada para nueva posición)
      { left: 55, top: 60, width: 35, height: 40 },
      // Zona central reducida para permitir más dispersión
      { left: 45, top: 35, width: 10, height: 10 },
      // Zona del v-alert (solo el área central del texto para permitir dots en márgenes)
      { left: 15, top: 88, width: 70, height: 25 },
    ]
  }

  // Verificar si una posición está en conflicto con zonas de exclusión
  private isPositionValid (left: number, top: number, size: number): boolean {
    const exclusionZones = this.getExclusionZones()
    const elementZone = {
      left: left - size / 2,
      top: top - size / 2,
      width: size,
      height: size,
    }

    return !exclusionZones.some(zone => {
      return !(elementZone.left > zone.left + zone.width ||
               elementZone.left + elementZone.width < zone.left ||
               elementZone.top > zone.top + zone.height ||
               elementZone.top + elementZone.height < zone.top)
    })
  }

  // Calcular posiciones inteligentes evitando colisiones con mejor dispersión
  private calculateSmartPositions (count: number, elementSize: number = 5): Array<{ left: number, top: number }> {
    const positions: Array<{ left: number, top: number }> = []
    const maxAttempts = 150
    const minDistance = elementSize + 8

    const quadrants = [
      [0, 45, 0, 45], [55, 100, 0, 45], [0, 45, 55, 100], [55, 100, 55, 100],
      [0, 25, 25, 75], [75, 100, 25, 75], [25, 75, 0, 25], [25, 75, 75, 100],
    ]

    for (let i = 0; i < count; i++) {
      const position = this.findValidPosition(i, positions, quadrants, elementSize, minDistance, maxAttempts)
      positions.push(position)
    }

    return positions
  }

  // Buscar posición válida para un elemento
  private findValidPosition (
    index: number,
    existingPositions: Array<{ left: number, top: number }>,
    quadrants: number[][],
    elementSize: number,
    minDistance: number,
    maxAttempts: number
  ): { left: number, top: number } {
    let attempts = 0
    const useQuadrant = index < quadrants.length

    while (attempts < maxAttempts) {
      const candidate = this.generatePosition(index, quadrants, useQuadrant)

      if (this.isPositionValid(candidate.left, candidate.top, elementSize) &&
          this.isDistanceValid(candidate, existingPositions, minDistance)) {
        return candidate
      }
      attempts++
    }

    // Posiciones de respaldo dispersas
    const fallbacks = [
      [5, 5], [95, 5], [5, 95], [95, 95], [5, 50], [95, 50], [50, 5], [50, 95],
      [25, 15], [75, 15], [25, 85], [75, 85], [15, 25], [85, 25], [15, 75], [85, 75],
    ]
    const fallback = fallbacks[index % fallbacks.length]
    return { left: fallback[0], top: fallback[1] }
  }

  // Generar posición candidata
  private generatePosition (index: number, quadrants: number[][], useQuadrant: boolean): { left: number, top: number } {
    if (useQuadrant) {
      const [leftMin, leftMax, topMin, topMax] = quadrants[index]
      return {
        left: Math.random() * (leftMax - leftMin) + leftMin,
        top: Math.random() * (topMax - topMin) + topMin,
      }
    }
    return { left: Math.random() * 95 + 2.5, top: Math.random() * 95 + 2.5 }
  }

  // Verificar distancia válida entre elementos
  private isDistanceValid (
    candidate: { left: number, top: number },
    positions: Array<{ left: number, top: number }>,
    minDistance: number
  ): boolean {
    return !positions.some(pos => {
      const distance = Math.sqrt(
        Math.pow(candidate.left - pos.left, 2) + Math.pow(candidate.top - pos.top, 2)
      )
      return distance < minDistance
    })
  }

  // Generar estilos dinámicos para las partículas flotantes
  getParticleStyle (index: number): Record<string, string> {
    if (!this.particlePositions) {
      this.particlePositions = this.calculateSmartPositions(12, 4)
    }

    const position = this.particlePositions[index - 1] || { left: 50, top: 50 }
    const delay = (index * 0.5) + 's'
    const duration = (2 + (index % 3)) + 's'

    return {
      left: position.left + '%',
      top: position.top + '%',
      animationDelay: delay,
      animationDuration: duration,
    }
  }

  // Método de herramientas flotantes eliminado según solicitud

  // Calcular posiciones para chispas de soldadura
  getSparkPositions (): Array<{ left: string, top: string }> {
    if (!this.sparkPositions) {
      // Las chispas deben estar cerca de los engranajes
      const gearPositions = [
        { left: 17, top: 10 }, // Cerca del engranaje 1
        { left: 20, top: 12 }, // Cerca del engranaje 1
        { left: 70, top: 30 }, // Cerca del engranaje 2
        { left: 25, top: 65 }, // Cerca del engranaje 3
        { left: 15, top: 8 }, // Cerca del engranaje 1
        { left: 22, top: 15 }, // Cerca del engranaje 1
      ]
      this.sparkPositions = gearPositions.map(pos => ({
        left: pos.left + '%',
        top: pos.top + '%',
      }))
    }
    return this.sparkPositions
  }
  }
